import { useEffect, useState, Fragment } from 'react';
import { Button, Checkbox, Divider, FormControlLabel, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import { Help, Warning } from '@mui/icons-material';
import CircularLoader from 'ui-component/CircularLoader';
import { Box } from '@mui/system';
import PropTypes from 'prop-types';

/**
 * @params
 *  isOpen (Boolean) = set modal open
 * onClose (Function) = set parent toggle to falsess
 */
export default function ConfirmationModal({ data, onClose }) {
    const [open, setOpen] = useState(false);
    const {
        isOpen,
        title,
        message,
        handleProceed,
        isProceed,
        fnClose,
        content,
        type = 'confirmation',
        confirmToProceed = false,
        confirmToProceedLabel
    } = data;
    const [loading, setLoading] = useState(false);
    const [confirm, setConfirm] = useState(false);

    const modalTheme = {
        cancel: {
            primary: '#f65f5f',
            hover: '#f65f5feb'
        },
        proceed: {
            primary: '#2ca58d',
            hover: '#298270'
        }
    };

    let colorVariant;
    let ConfirmationIcon = Help;
    switch (type) {
        case 'confirmation':
            colorVariant = modalTheme['proceed'];
            break;
        case 'confirmDelete':
            colorVariant = modalTheme['cancel'];
            ConfirmationIcon = Warning;
            break;
        default:
            colorVariant = modalTheme['proceed'];
            break;
    }

    useEffect(() => {
        setLoading(isProceed);
    }, [isProceed]);

    useEffect(() => {
        setOpen(isOpen || false);
    }, [isOpen]);

    const handleClose = () => {
        setOpen(false);
        onClose();

        if (typeof fnClose !== 'undefined') {
            fnClose(); // handle from your component if cancel was click
        }
    };

    return (
        <Fragment>
            <Dialog
                open={open || false}
                size="small"
                // onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                sx={{
                    '.MuiPaper-root': {
                        p: 2,
                        width: '22rem',
                        borderTop: `5px solid ${colorVariant.primary}`,
                        minWidth: '28rem'
                    }
                }}
                aria-describedby="alert-dialog-description"
            >
                <DialogContent>
                    <DialogContentText id="alert-dialog-description" component="div" sx={{ display: 'flex', gap: '1.5rem', pt: '6px' }}>
                        <ConfirmationIcon
                            sx={{
                                fontSize: '4rem',
                                color: colorVariant.primary
                            }}
                        />
                        <Box
                            sx={{
                                textAlign: 'left',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start'
                            }}
                        >
                            {title && (
                                <Typography
                                    sx={{
                                        textAlign: 'left',
                                        fontWeight: 500,
                                        fontSize: '1.25rem'
                                    }}
                                >
                                    {title}
                                </Typography>
                            )}
                            {message && (
                                <Typography
                                    sx={{
                                        textAlign: 'left',
                                        lineHeight: 1.2,
                                        fontSize: '0.86rem',
                                        mt: 1.4
                                    }}
                                >
                                    {message}
                                </Typography>
                            )}

                            {content}

                            {confirmToProceed && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        mt: 2
                                    }}
                                >
                                    <FormControlLabel
                                        control={<Checkbox checked={confirm} onChange={(e) => setConfirm(e.target.checked)} />}
                                        label={<Typography variant="body2">{confirmToProceedLabel || 'Accept and continue.'}</Typography>}
                                    />
                                </Box>
                            )}
                        </Box>
                    </DialogContentText>
                </DialogContent>
                <Divider sx={{ my: 0.5 }} />
                <DialogActions
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-evenly',
                        alignItems: 'center',
                        mt: 1
                    }}
                >
                    <Button
                        onClick={() => {
                            setLoading(true);
                            handleProceed();
                        }}
                        autoFocus
                        variant="contained"
                        size="medium"
                        // fullWidth
                        sx={{
                            background: modalTheme['proceed'].primary,
                            '&:hover': {
                                background: modalTheme['proceed'].hover
                            }
                        }}
                        disabled={(confirmToProceed && !confirm) || loading}
                    >
                        {loading ? (
                            <>
                                <CircularLoader size={24} minHeight="0rem" />
                                <Box paddingLeft={1}>Submitting</Box>
                            </>
                        ) : (
                            'Proceed'
                        )}
                    </Button>
                    <Button
                        onClick={handleClose}
                        autoFocus
                        variant="contained"
                        size="medium"
                        // fullWidth
                        sx={{
                            background: modalTheme['cancel'].primary,
                            '&:hover': {
                                background: modalTheme['cancel'].hover
                            }
                        }}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </Fragment>
    );
}

ConfirmationModal.propTypes = {
    data: PropTypes.any,
    onClose: PropTypes.func
};
